/**
 * Banner text from the service's connection phase and backoff state.
 * "next try in N s" shows only while a retry is really scheduled.
 *
 * @param {{ phase?: 'open'|'connecting'|'waiting'|'stopped', attempt?: number, nextRetryMs?: number }|null|undefined} info
 */
export function reconnectBannerText(info) {
  const seconds = Math.ceil((info?.nextRetryMs ?? 0) / 1000);
  const phase = info?.phase ?? (seconds > 0 ? 'waiting' : 'stopped');
  if (phase === 'connecting' || (phase === 'waiting' && seconds <= 0)) return 'Connecting…';
  if (phase === 'waiting') return `Reconnecting, attempt ${info?.attempt ?? 1}, next try in ${seconds} s`;
  return 'Connection lost. Tap Reconnect now to try again.';
}

/**
 * The connect-failure message while WS_CONSTRUCT_FAILED stands (the browser
 * could not even make the socket, so a Reconnect tap fails the same way and
 * only a reload helps). Null for any other error, so callers keep the usual
 * reconnect hint. Pure, so the room page and the game banner share it.
 *
 * @param {{ code?: string, message?: string }|null|undefined} serviceError
 */
export function connectErrorBannerText(serviceError) {
  if (!serviceError || typeof serviceError !== 'object') return null;
  if (serviceError.code !== 'WS_CONSTRUCT_FAILED') return null;
  return typeof serviceError.message === 'string' && serviceError.message ? serviceError.message : null;
}

/**
 * Room page: the connect-failure message while inside a room, else null.
 * The home, create and join steps already show ws.error themselves, so only
 * the room page needs this.
 *
 * @param {{ status?: string, roomCode?: string|null, error?: { code?: string, message?: string }|null }} s
 */
export function roomPageConnectErrorText({ status, roomCode, error }) {
  if (status !== 'in_room' || !roomCode) return null;
  return connectErrorBannerText(error);
}

/** Why the bid, play and discard controls are off while the socket is down. */
export const OFFLINE_CONTROLS_TEXT = 'No connection. Moves are paused until it is back.';
/** Shown while a move waits for the server's answer. */
export const SENDING_TEXT = 'Sending…';

/**
 * Why moves are locked now, or null when they are not. No connection
 * wins: it says what the player is waiting for.
 * @param {{ connectionLost: boolean, sending: boolean }} s
 */
export function moveLockText({ connectionLost, sending }) {
  if (connectionLost) return OFFLINE_CONTROLS_TEXT;
  if (sending) return SENDING_TEXT;
  return null;
}

/** Continue or Back to lobby while the socket is down. */
export const OFFLINE_BUTTON_TEXT = 'No connection';

/**
 * Continue and Back to lobby. No connection wins: while the socket is down
 * the button reads "No connection" and is locked, whatever the ready says
 * (its answer cannot arrive offline). Online, "Sending…" while the ready
 * waits for its answer; the waiting text only once the server has it. A
 * null label keeps the button's own ("Continue", "Back to lobby").
 * @param {{ readyState: 'idle'|'sending'|'waiting', connectionLost: boolean, sending: boolean, waitingText: string|null }} s
 * @returns {{ locked: boolean, label: string|null, waiting: boolean }}
 */
export function readyButtonState({ readyState, connectionLost, sending, waitingText }) {
  if (connectionLost) return { locked: true, label: OFFLINE_BUTTON_TEXT, waiting: false };
  if (readyState === 'waiting') return { locked: true, label: waitingText, waiting: true };
  if (readyState === 'sending' || sending) return { locked: true, label: SENDING_TEXT, waiting: false };
  return { locked: false, label: null, waiting: false };
}

/**
 * Whether a hand card can be picked: a legal card on my trick turn, or
 * any card in my discard step. Nothing while moves are locked.
 * @param {{ locked: boolean, isTrickPlay: boolean, isMyTurn: boolean, isMyDealerDiscard: boolean, legalPlays: string[], hit: { card_id: string, legal?: boolean } }} s
 * @returns {{ interactive: boolean, legal: boolean }}
 */
export function handCardHitState({ locked, isTrickPlay, isMyTurn, isMyDealerDiscard, legalPlays, hit }) {
  if (locked) return { interactive: false, legal: false };
  if (isTrickPlay && isMyTurn) {
    const legal = legalPlays.length > 0 ? legalPlays.includes(hit.card_id) : !!hit.legal;
    return { interactive: legal, legal };
  }
  if (isMyDealerDiscard) return { interactive: true, legal: true };
  return { interactive: false, legal: false };
}

/**
 * Whether the dealer can pick the up-card in the discard step.
 * @param {{ locked: boolean }} s
 * @returns {{ interactive: boolean, legal: boolean }}
 */
export function upcardHitState({ locked }) {
  return locked ? { interactive: false, legal: false } : { interactive: true, legal: true };
}

export function leaveConfirmText() {
  return "Leave this game? A bot takes your seat and you can't come back to this game.";
}

/**
 * Wrap `fn` so it runs at most once (a double tap on Leave sends one leave).
 * @template {(...args: any[]) => void} F
 * @param {F} fn
 * @returns {(...args: Parameters<F>) => void}
 */
export function onceOnly(fn) {
  let done = false;
  return (...args) => {
    if (done) return;
    done = true;
    fn(...args);
  };
}

/** What a key press in the leave confirm does: Escape means Stay. */
export function leaveConfirmKeyAction(key) {
  return key === 'Escape' ? 'stay' : null;
}

export function ownSeatBotNote(snapshot, mySeatIndex) {
  if (mySeatIndex == null) return '';
  const occupant = snapshot?.seat_occupants?.[mySeatIndex + 1];
  return occupant?.bot_held && occupant?.reclaim_at === 'next_hand_end'
    ? 'A bot is playing your seat. You get it back at the end of this hand.'
    : '';
}
